import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from 'context/AuthContext';
import favoriteService from 'services/favoriteService';
import { FavoriteProduct } from 'types/customer';

interface FavoritesContextValue {
  favorites: FavoriteProduct[];
  loading: boolean;
  isFavorite: (sku: string) => boolean;
  toggleFavorite: (sku: string) => Promise<boolean>;
  refreshFavorites: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

const skuKey = (sku: string) => sku.trim().toLowerCase();

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  const [favorites, setFavorites] = useState<FavoriteProduct[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavorites([]);
      return;
    }
    setLoading(true);
    try {
      setFavorites(await favoriteService.getFavorites());
    } catch {
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refreshFavorites();
  }, [refreshFavorites]);

  const isFavorite = useCallback(
    (sku: string) => favorites.some((item) => skuKey(item.productId) === skuKey(sku)),
    [favorites]
  );

  const toggleFavorite = useCallback(
    async (sku: string) => {
      const code = sku.trim();
      if (!code || !isAuthenticated) return false;
      const currently = favorites.some((item) => skuKey(item.productId) === skuKey(code));
      if (currently) {
        await favoriteService.removeFavorite(code);
        setFavorites((prev) => prev.filter((item) => skuKey(item.productId) !== skuKey(code)));
        return false;
      }
      await favoriteService.addFavorite(code);
      setFavorites((prev) =>
        prev.some((item) => skuKey(item.productId) === skuKey(code))
          ? prev
          : [
              ...prev,
              {
                productId: code,
                slug: code,
                name: code,
                price: 0,
                addedAt: new Date().toISOString(),
              },
            ]
      );
      return true;
    },
    [favorites, isAuthenticated]
  );

  const value = useMemo(
    () => ({ favorites, loading, isFavorite, toggleFavorite, refreshFavorites }),
    [favorites, loading, isFavorite, toggleFavorite, refreshFavorites]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavoritesContext = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavoritesContext debe usarse dentro de FavoritesProvider');
  return ctx;
};
